import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import i18n from '../i18n/i18n';

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (lang: string) => void;
  getTextInCurrentLanguage: (text: { en: string; es: string }) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    // Check for saved language preference in localStorage
    const savedLang = localStorage.getItem('preferredLanguage');
    return savedLang || 'es'; // Default to Spanish
  });

  useEffect(() => {
    // Initialize i18n with the selected language
    i18n.changeLanguage(currentLanguage);
  }, [currentLanguage]);

  const changeLanguage = (lang: string) => {
    setCurrentLanguage(lang);
    i18n.changeLanguage(lang);
    // Save preference to localStorage
    localStorage.setItem('preferredLanguage', lang);
  };

  const getTextInCurrentLanguage = (text: { en: string; es: string }): string => {
    return text[currentLanguage as keyof typeof text] || text.en;
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage, getTextInCurrentLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};